/* eslint-disable */
/**
 * HjerneGrafPanel.jsx — Interaktiv videns-graf formet som en menneskehjerne
 *
 * Standard: WebGL-renderer (HjerneGrafWebGL) — ALLE ~34k noder + alle kanter
 *   Datakilde: /api/os/hjerne/alle-noder-graf-alle (ingen limit — alle ~34k noder)
 *   Renderer: Three.js InstancedMesh — ét GPU draw-call for alle noder
 *   Kanter: Three.js LineSegments — ét GPU draw-call for alle ~37k kanter
 *   Koordinater: precomputed fra os.graf_layout (ingen force simulation)
 *
 * D3-renderer (bevaret som fallback — bruges ikke længere som default):
 *   Datakilde: /api/os/hjerne/alle-noder-graf → maks 1.200 udvalgte noder
 *   Renderer: D3 force simulation + canvas
 *
 * Regler:
 *   - Ingen hardkodede tal — alt fra DB via API
 *   - Dansk sprog konsekvent (æøå korrekt)
 *   - Ingen emojis
 */

import React, {
  useRef, useEffect, useState, useCallback, useMemo
} from 'react';
import * as d3 from 'd3';
import {
  STATUS, TEKST, BG, KANT, RADIUS, FONT,
} from '../komponenter/tokens';

// WebGL er nu STANDARD — ingen build-flag nødvendig
const HjerneGrafWebGLLazy = React.lazy(() => import('./HjerneGrafWebGL'));

// ─── Farvepalette per node-type ───────────────────────────────────────────────

const TYPE_FARVE = {
  fakta:                '#3b82f6',   // blå
  begreb:               '#8b5cf6',   // lilla
  entitet:              '#22c55e',   // grøn
  regel:                '#f59e0b',   // amber
  procedure:            '#06b6d4',   // cyan
  statistik:            '#f97316',   // orange
  'klient-meta':        '#ec4899',   // pink
  email_modtaget:       '#6366f1',   // indigo
  email_udsendt:        '#a78bfa',   // lilla-lys
  'copilot-chat':       '#14b8a6',   // teal
  regnskab:             '#84cc16',   // lime
  marketing:            '#fb7185',   // rose
  'juridisk-automation':'#fbbf24',   // gul
  hjerne_fil:           '#94a3b8',   // grå — hjerne-filer
  andet:                '#475569',   // mørkgrå
};

function nodeFarve(type) {
  return TYPE_FARVE[type] || TYPE_FARVE.andet;
}

// ─── Hemisfære-tildeling per type ─────────────────────────────────────────────
// Venstre hemisfære: analytisk/juridisk/fakta
// Højre hemisfære: kreativt/kommunikation/relation

const VENSTRE_TYPER = new Set([
  'fakta', 'regel', 'procedure', 'statistik', 'juridisk-automation', 'regnskab',
]);
const HOEJRE_TYPER = new Set([
  'begreb', 'entitet', 'marketing', 'klient-meta', 'email_modtaget',
  'email_udsendt', 'copilot-chat', 'hjerne_fil',
]);

function hemisfaere(type) {
  if (VENSTRE_TYPER.has(type)) return 'venstre';
  if (HOEJRE_TYPER.has(type)) return 'hoejre';
  return Math.random() > 0.5 ? 'venstre' : 'hoejre';
}

// ─── Type-mærkat (til detalje-panel) ─────────────────────────────────────────

const TYPE_MAERKAT = {
  fakta:                'Fakta',
  begreb:               'Begreb',
  entitet:              'Entitet',
  regel:                'Regel',
  procedure:            'Procedure',
  statistik:            'Statistik',
  'klient-meta':        'Klient-meta',
  email_modtaget:       'Modtaget mail',
  email_udsendt:        'Udsendt mail',
  'copilot-chat':       'Copilot-chat',
  regnskab:             'Regnskab',
  marketing:            'Marketing',
  'juridisk-automation':'Jur. automation',
  hjerne_fil:           'Hjerne-fil',
  andet:                'Andet',
};

function typeMaerkat(type) {
  return TYPE_MAERKAT[type] || type;
}

// ─── Konstanter for graf-dimensioner ────────────────────────────────────────

const MAX_NODER   = 1200;   // max synlige noder
const MAX_KANTER  = 600;    // max synlige kanter

// ─── Hjerne-silhouet SVG-path (normaliseret 0-1 koordinater) ────────────────
// Tegner en stiliseret to-hemisfærer-hjerne set fra oven.
// Venstre hemisfære: x 0.05–0.47, Højre: x 0.53–0.95
// Bruges kun som dekorativ baggrundsform.

function tegneHjernePath(w, h) {
  const s = (x, y) => `${x * w},${y * h}`;
  // Venstre hemisfære (oval med lobes)
  const venstre = [
    `M ${s(0.48, 0.50)}`,
    `C ${s(0.48, 0.18)} ${s(0.35, 0.05)} ${s(0.22, 0.08)}`,
    `C ${s(0.08, 0.10)} ${s(0.02, 0.22)} ${s(0.04, 0.38)}`,
    `C ${s(0.05, 0.52)} ${s(0.10, 0.65)} ${s(0.18, 0.72)}`,
    `C ${s(0.25, 0.80)} ${s(0.35, 0.82)} ${s(0.40, 0.80)}`,
    `C ${s(0.44, 0.78)} ${s(0.48, 0.72)} ${s(0.48, 0.62)}`,
    `Z`,
  ].join(' ');

  // Højre hemisfære (spejlvendt)
  const hoejre = [
    `M ${s(0.52, 0.50)}`,
    `C ${s(0.52, 0.18)} ${s(0.65, 0.05)} ${s(0.78, 0.08)}`,
    `C ${s(0.92, 0.10)} ${s(0.98, 0.22)} ${s(0.96, 0.38)}`,
    `C ${s(0.95, 0.52)} ${s(0.90, 0.65)} ${s(0.82, 0.72)}`,
    `C ${s(0.75, 0.80)} ${s(0.65, 0.82)} ${s(0.60, 0.80)}`,
    `C ${s(0.56, 0.78)} ${s(0.52, 0.72)} ${s(0.52, 0.62)}`,
    `Z`,
  ].join(' ');

  return { venstre, hoejre };
}

// ─── Konverter D3-koordinater til canvas-koordinater ─────────────────────────

function projektér(x, y, transform) {
  return [
    transform.x + x * transform.k,
    transform.y + y * transform.k,
  ];
}

// ─── Detalje-panel komponent ─────────────────────────────────────────────────

function DetaljePanel({ node, kanter, alleNoder, onLuk }) {
  if (!node) return null;

  const farve = nodeFarve(node.type);
  const naboer = kanter
    .filter(k => k.source === node.id || k.target === node.id)
    .map(k => {
      const naboid = k.source === node.id ? k.target : k.source;
      const nabo = alleNoder.find(n => n.id === naboid);
      return { nabo, relation: k.relation, styrke: k.styrke };
    })
    .filter(n => n.nabo)
    .slice(0, 8);

  return (
    <div style={{
      position:        'absolute',
      top:             16,
      right:           16,
      width:           280,
      maxHeight:       480,
      overflowY:       'auto',
      background:      'rgba(6,9,23,0.97)',
      border:          `1px solid ${farve}40`,
      borderRadius:    RADIUS.kort,
      padding:         '16px',
      backdropFilter:  'blur(20px)',
      boxShadow:       `0 0 24px ${farve}20, 0 4px 24px rgba(0,0,0,0.6)`,
      zIndex:          10,
      fontFamily:      FONT.family,
    }}>
      {/* Luk-knap */}
      <button
        onClick={onLuk}
        style={{
          position:   'absolute',
          top:        10,
          right:      10,
          background: 'none',
          border:     'none',
          color:      TEKST.sekundær,
          cursor:     'pointer',
          fontSize:   16,
          lineHeight: 1,
          padding:    2,
        }}
        aria-label="Luk"
      >
        x
      </button>

      {/* Type-badge */}
      <div style={{
        display:       'inline-block',
        background:    `${farve}18`,
        border:        `1px solid ${farve}40`,
        borderRadius:  RADIUS.badge,
        padding:       '2px 8px',
        fontSize:      10,
        color:         farve,
        fontWeight:    700,
        marginBottom:  8,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}>
        {typeMaerkat(node.type)}
      </div>

      {/* Titel */}
      <div style={{
        fontSize:   15,
        fontWeight: 700,
        color:      TEKST.primær,
        lineHeight: 1.35,
        marginBottom: 8,
      }}>
        {node.titel}
      </div>

      {/* Beskrivelse */}
      {node.beskrivelse && (
        <div style={{
          fontSize:     12,
          color:        TEKST.sekundær,
          lineHeight:   1.5,
          marginBottom: 12,
          borderLeft:   `2px solid ${farve}40`,
          paddingLeft:  8,
        }}>
          {node.beskrivelse.slice(0, 200)}{node.beskrivelse.length > 200 ? '...' : ''}
        </div>
      )}

      {/* Meta */}
      {node.relevans > 0 && (
        <div style={{ fontSize: 11, color: TEKST.dæmpet, marginBottom: 10 }}>
          Relevans: {node.relevans.toFixed(1)}
        </div>
      )}

      {/* Forbindelser */}
      {naboer.length > 0 && (
        <div>
          <div style={{
            fontSize:     10,
            fontWeight:   700,
            color:        TEKST.sekundær,
            textTransform:'uppercase',
            letterSpacing:'0.07em',
            marginBottom: 8,
          }}>
            Forbindelser ({naboer.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {naboer.map(({ nabo, relation }, i) => (
              <div key={i} style={{
                display:      'flex',
                alignItems:   'flex-start',
                gap:          6,
                fontSize:     11,
                background:   'rgba(255,255,255,0.03)',
                borderRadius: RADIUS.badge,
                padding:      '4px 6px',
              }}>
                <div style={{
                  width:        6,
                  height:       6,
                  borderRadius: '50%',
                  background:   nodeFarve(nabo.type),
                  flexShrink:   0,
                  marginTop:    3,
                }} />
                <div>
                  <span style={{ color: TEKST.primær }}>{nabo.titel?.slice(0, 50)}</span>
                  {relation && (
                    <span style={{ color: TEKST.dæmpet, marginLeft: 4 }}>
                      · {relation.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {naboer.length === 0 && (
        <div style={{ fontSize: 11, color: TEKST.dæmpet, fontStyle: 'italic' }}>
          Ingen kanter til denne node i vidensgraf
        </div>
      )}
    </div>
  );
}

// ─── Legend ──────────────────────────────────────────────────────────────────

function Legend({ typeAntal }) {
  const vis = Object.entries(typeAntal)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  return (
    <div style={{
      position:       'absolute',
      bottom:         16,
      left:           16,
      background:     'rgba(6,9,23,0.90)',
      border:         `1px solid ${KANT.standard}`,
      borderRadius:   RADIUS.kort,
      padding:        '10px 14px',
      backdropFilter: 'blur(16px)',
      maxHeight:      280,
      overflowY:      'auto',
      zIndex:         5,
      fontFamily:     FONT.family,
    }}>
      <div style={{
        fontSize:      10,
        fontWeight:    700,
        color:         TEKST.sekundær,
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        marginBottom:  8,
      }}>
        Node-typer
      </div>
      {vis.map(([type, antal]) => (
        <div key={type} style={{
          display:      'flex',
          alignItems:   'center',
          gap:          7,
          marginBottom: 5,
          fontSize:     11,
        }}>
          <div style={{
            width:        9,
            height:       9,
            borderRadius: '50%',
            background:   nodeFarve(type),
            flexShrink:   0,
          }} />
          <span style={{ color: TEKST.primær, minWidth: 90 }}>{typeMaerkat(type)}</span>
          <span style={{ color: TEKST.dæmpet, marginLeft: 'auto', paddingLeft: 8 }}>
            {antal.toLocaleString('da-DK')}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Søgefelt ────────────────────────────────────────────────────────────────

function Søgefelt({ soegTekst, onChange, matchAntal }) {
  return (
    <div style={{
      position:   'absolute',
      top:        16,
      left:       16,
      zIndex:     10,
      display:    'flex',
      alignItems: 'center',
      gap:        8,
      fontFamily: FONT.family,
    }}>
      <input
        type="text"
        value={soegTekst}
        onChange={e => onChange(e.target.value)}
        placeholder="Søg i vidensgraf..."
        style={{
          background:   'rgba(6,9,23,0.95)',
          border:       `1px solid ${KANT.aktiv}`,
          borderRadius: RADIUS.knap,
          padding:      '7px 14px',
          fontSize:     12,
          color:        TEKST.primær,
          outline:      'none',
          width:        200,
          fontFamily:   FONT.family,
        }}
      />
      {soegTekst && matchAntal !== null && (
        <span style={{ fontSize: 11, color: TEKST.sekundær }}>
          {matchAntal} match
        </span>
      )}
    </div>
  );
}

// ─── KPI-stribe ──────────────────────────────────────────────────────────────

function KpiStribe({ grafData, rawAntalNoder, rawAntalKanter }) {
  const vis = [
    { label: 'Synlige noder',    val: grafData?.noder?.length ?? null },
    { label: 'Total viden-base', val: rawAntalNoder },
    { label: 'Kanter',           val: rawAntalKanter },
  ];
  return (
    <div style={{
      display:      'flex',
      gap:          0,
      background:   'rgba(6,9,23,0.85)',
      border:       `1px solid ${KANT.standard}`,
      borderRadius: RADIUS.kort,
      overflow:     'hidden',
      marginBottom: 0,
      fontFamily:   FONT.family,
    }}>
      {vis.map(({ label, val }, i) => (
        <div key={i} style={{
          flex:           1,
          padding:        '10px 16px',
          borderRight:    i < vis.length - 1 ? `1px solid ${KANT.fin}` : 'none',
          textAlign:      'center',
        }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: TEKST.primær, lineHeight: 1.1 }}>
            {val !== null && val !== undefined
              ? val.toLocaleString('da-DK')
              : <span style={{ color: TEKST.dæmpet }}>...</span>
            }
          </div>
          <div style={{ fontSize: 10, color: TEKST.sekundær, marginTop: 2 }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Hoved-graf-komponent (D3 canvas-renderer) ───────────────────────────────

function HjerneGrafPanelD3() {
  const canvasRef   = useRef(null);
  const simRef      = useRef(null);
  const transformRef= useRef(d3.zoomIdentity);
  const nodeRef     = useRef([]);
  const kantRef     = useRef([]);
  const hovNodRef   = useRef(null);
  const rafRef      = useRef(null);
  const zoomRef     = useRef(null);

  const [noder,      setNoder]      = useState([]);
  const [kanter,     setKanter]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [fejl,       setFejl]       = useState(null);
  const [valgtNode,  setValgtNode]  = useState(null);
  const [soegTekst,  setSoegTekst]  = useState('');
  const [matchAntal, setMatchAntal] = useState(null);
  const [vaekst,     setVaekst]     = useState(null);
  const [typeAntal,  setTypeAntal]  = useState({});
  const [dims,       setDims]       = useState({ w: 900, h: 600 });

  const containerRef = useRef(null);

  // ── Resize observer ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        if (width > 0 && height > 0) setDims({ w: width, h: height });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // ── Hent data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    async function hent() {
      setLoading(true);
      setFejl(null);
      try {
        const [noderSvar, vaekstSvar] = await Promise.all([
          fetch('/api/os/hjerne/alle-noder-graf', { credentials: 'include' }).then(r => r.json()),
          fetch('/api/os/hjerne/vaekst',           { credentials: 'include' }).then(r => r.json()).catch(() => null),
        ]);

        const alleNoder = noderSvar.noder || [];
        setVaekst(vaekstSvar?.stats || null);

        // ── Vælg repræsentative noder (max MAX_NODER) ──────────────────────
        // Strategi: tag top-(relevans) per type, samlet max MAX_NODER
        const perType = {};
        for (const n of alleNoder) {
          if (!perType[n.type]) perType[n.type] = [];
          perType[n.type].push(n);
        }

        // Sorter per type efter relevans, tag proportionalt antal
        const typer    = Object.keys(perType);
        const kvoterUd = {};
        const totalTyper = typer.length;
        const kvoterPrType = Math.max(10, Math.floor(MAX_NODER / totalTyper));

        const udvalgte = [];
        for (const t of typer) {
          const sorteret = perType[t].sort((a, b) => (b.relevans || 0) - (a.relevans || 0));
          kvoterUd[t] = Math.min(sorteret.length, kvoterPrType);
          udvalgte.push(...sorteret.slice(0, kvoterUd[t]));
        }

        // Klip til MAX_NODER
        const synligeNoder = udvalgte.slice(0, MAX_NODER);

        // Type-fordeling til legend
        const ta = {};
        for (const n of synligeNoder) {
          ta[n.type] = (ta[n.type] || 0) + 1;
        }
        setTypeAntal(ta);

        // ── Tilskyd noder i hemisfærer ─────────────────────────────────────
        const { w, h } = dims;
        const cx = w / 2;
        const cy = h / 2;
        const hRadius = Math.min(w * 0.38, h * 0.42);

        // Byg id-sæt til kant-filtrering
        const nodeIdSaet = new Set(synligeNoder.map(n => n.id));

        // ── Tilskyd startpositioner i hjerne-form ─────────────────────────
        const d3Noder = synligeNoder.map(n => {
          const side = hemisfaere(n.type);
          const sideX = side === 'venstre' ? cx - hRadius * 0.5 : cx + hRadius * 0.5;
          // Spredt tilfældig start inden for hemisfære
          const vinkel   = Math.random() * Math.PI - Math.PI / 2;
          const r         = Math.random() * hRadius * 0.85;
          return {
            ...n,
            x: sideX + r * Math.cos(vinkel) * 0.6,
            y: cy    + r * Math.sin(vinkel),
            side,
          };
        });

        nodeRef.current = d3Noder;
        setNoder(d3Noder);

        // ── Hent kanter (vidensgraf) ─────────────────────────────────────
        // Der er 306 kanter i vidensgraf — alle hentes
        let kantData = [];
        try {
          const kantSvar = await fetch('/api/os/hjerne/vidensgraf-kanter', { credentials: 'include' });
          if (kantSvar.ok) {
            const kj = await kantSvar.json();
            kantData = kj.kanter || [];
          }
        } catch (_) {
          // Endpoint eksisterer måske ikke endnu — vi fortsætter uden kanter
        }

        // Filtrer kanter til synlige noder
        const synligeKanter = kantData
          .filter(k => nodeIdSaet.has(k.source) && nodeIdSaet.has(k.target))
          .slice(0, MAX_KANTER);

        kantRef.current = synligeKanter;
        setKanter(synligeKanter);

        // ── Start D3 force simulation ────────────────────────────────────
        opstartSimulation(d3Noder, synligeKanter, w, h);

      } catch (err) {
        console.error('[HjerneGraf]', err);
        setFejl('Kunne ikke hente graf-data: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    hent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dims.w, dims.h]);

  // ── D3 force simulation ──────────────────────────────────────────────────
  const opstartSimulation = useCallback((d3Noder, d3Kanter, w, h) => {
    if (simRef.current) simRef.current.stop();

    const cx = w / 2;
    const cy = h / 2;

    // Link-format: D3 kræver objekt med source/target (id-strenge)
    const links = d3Kanter.map(k => ({ ...k }));

    const sim = d3.forceSimulation(d3Noder)
      // Tiltrækning mod hemisfære-centre
      .force('x', d3.forceX(n => {
        return n.side === 'venstre'
          ? cx - w * 0.21
          : cx + w * 0.21;
      }).strength(0.08))
      .force('y', d3.forceY(cy).strength(0.05))
      // Afstand mellem noder
      .force('collide', d3.forceCollide(d => {
        const r = nodeRadius(d);
        return r + 2.5;
      }).strength(0.7))
      // Kanter
      .force('link', d3.forceLink(links)
        .id(n => n.id)
        .distance(60)
        .strength(0.3)
      )
      // Svag central tyngde
      .force('charge', d3.forceManyBody().strength(-35).distanceMax(200))
      // Hjerne-form: begræns noder inden for en oval
      .force('boundary', () => {
        for (const n of d3Noder) {
          const hx = n.side === 'venstre' ? cx - w * 0.23 : cx + w * 0.23;
          const dx = (n.x - hx) / (w * 0.24);
          const dy = (n.y - cy) / (h * 0.38);
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 1) {
            n.x = hx + (n.x - hx) / dist;
            n.y = cy  + (n.y - cy)  / dist;
          }
        }
      })
      .alphaDecay(0.025)
      .on('tick', tegn)
      .on('end', tegn);

    simRef.current = sim;
    nodeRef.current = d3Noder;
    kantRef.current = links;
  }, []);

  // ── Radius per node ───────────────────────────────────────────────────────
  function nodeRadius(n) {
    if (n.relevans > 8) return 7;
    if (n.relevans > 4) return 5;
    if (n.relevans > 1) return 4;
    return 3;
  }

  // ── Canvas tegne-loop ─────────────────────────────────────────────────────
  const tegn = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx   = canvas.getContext('2d');
    const { w, h } = dims;
    const t     = transformRef.current;
    const nodes = nodeRef.current;
    const links = kantRef.current;
    const hov   = hovNodRef.current;
    const søg   = soegTekst.toLowerCase().trim();

    ctx.save();
    ctx.clearRect(0, 0, w, h);

    // ── Baggrund ──────────────────────────────────────────────────────
    ctx.fillStyle = BG.app;
    ctx.fillRect(0, 0, w, h);

    ctx.translate(t.x, t.y);
    ctx.scale(t.k, t.k);

    // ── Hjerne-silhouet (baggrunds-aura) ──────────────────────────────
    const cx = w / 2;
    const cy = h / 2;
    const rX = w * 0.24;
    const rY = h * 0.38;

    // Venstre hemisfære aura
    ctx.beginPath();
    ctx.ellipse(cx - w * 0.23, cy, rX, rY, 0, 0, Math.PI * 2);
    const gradV = ctx.createRadialGradient(cx - w * 0.23, cy, 0, cx - w * 0.23, cy, rX);
    gradV.addColorStop(0, 'rgba(139,92,246,0.04)');
    gradV.addColorStop(0.7, 'rgba(59,130,246,0.02)');
    gradV.addColorStop(1, 'rgba(59,130,246,0)');
    ctx.fillStyle = gradV;
    ctx.fill();
    ctx.strokeStyle = 'rgba(139,92,246,0.12)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Højre hemisfære aura
    ctx.beginPath();
    ctx.ellipse(cx + w * 0.23, cy, rX, rY, 0, 0, Math.PI * 2);
    const gradH = ctx.createRadialGradient(cx + w * 0.23, cy, 0, cx + w * 0.23, cy, rX);
    gradH.addColorStop(0, 'rgba(6,182,212,0.04)');
    gradH.addColorStop(0.7, 'rgba(59,130,246,0.02)');
    gradH.addColorStop(1, 'rgba(59,130,246,0)');
    ctx.fillStyle = gradH;
    ctx.fill();
    ctx.strokeStyle = 'rgba(6,182,212,0.12)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Corpus callosum (vertikal bro)
    ctx.beginPath();
    ctx.moveTo(cx, cy - h * 0.28);
    ctx.lineTo(cx, cy + h * 0.28);
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 8;
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.03)';
    ctx.lineWidth = 24;
    ctx.stroke();

    // ── Kanter ────────────────────────────────────────────────────────
    if (links.length > 0) {
      ctx.globalAlpha = 0.18;
      ctx.lineWidth = 0.8;
      for (const lnk of links) {
        const s = lnk.source;
        const tg = lnk.target;
        if (!s?.x || !tg?.x) continue;
        const fremhæv = hov && (s.id === hov.id || tg.id === hov.id);
        ctx.globalAlpha = fremhæv ? 0.7 : 0.14;
        ctx.lineWidth   = fremhæv ? 1.5 : 0.7;
        ctx.strokeStyle = fremhæv
          ? nodeFarve(s.type)
          : 'rgba(255,255,255,0.5)';
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(tg.x, tg.y);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    // ── Noder ────────────────────────────────────────────────────────
    const harSoeg = søg.length >= 2;

    for (const n of nodes) {
      if (!n.x || !n.y) continue;
      const r     = nodeRadius(n);
      const farve = nodeFarve(n.type);
      const erHov = hov && n.id === hov.id;
      const erMatch = harSoeg && n.titel?.toLowerCase().includes(søg);
      const dæmpet  = harSoeg && !erMatch;

      ctx.globalAlpha = dæmpet ? 0.12 : (erHov ? 1 : 0.82);

      // Glød ved hover eller søgematch
      if (erHov || erMatch) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, r + 4, 0, Math.PI * 2);
        const gGrad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, r + 4);
        gGrad.addColorStop(0, farve + '50');
        gGrad.addColorStop(1, farve + '00');
        ctx.fillStyle = gGrad;
        ctx.fill();
      }

      // Node-cirkel
      ctx.beginPath();
      ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
      ctx.fillStyle = farve;
      ctx.fill();

      // Kant
      ctx.beginPath();
      ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
      ctx.strokeStyle = erHov ? '#fff' : farve + '60';
      ctx.lineWidth = erHov ? 1.5 : 0.5;
      ctx.stroke();

      // Label ved hover
      if (erHov) {
        ctx.globalAlpha = 1;
        const tekst = n.titel?.slice(0, 40) || '';
        ctx.font = `bold 11px ${FONT.family}`;
        const tm = ctx.measureText(tekst);
        const px = n.x + r + 6;
        const py = n.y + 4;

        ctx.fillStyle = 'rgba(6,9,23,0.90)';
        ctx.fillRect(px - 3, py - 13, tm.width + 8, 18);

        ctx.fillStyle = '#e2e8f0';
        ctx.fillText(tekst, px + 1, py);
      }

      // Label ved søgematch (stor nok node)
      if (erMatch && r >= 4) {
        ctx.globalAlpha = 1;
        const tekst = n.titel?.slice(0, 30) || '';
        ctx.font = `bold 10px ${FONT.family}`;
        const tm = ctx.measureText(tekst);
        const px = n.x + r + 4;
        const py = n.y + 3;
        ctx.fillStyle = 'rgba(6,9,23,0.85)';
        ctx.fillRect(px - 2, py - 12, tm.width + 6, 16);
        ctx.fillStyle = farve;
        ctx.fillText(tekst, px + 1, py);
      }

      ctx.globalAlpha = 1;
    }

    // ── Hemisfære-labels ─────────────────────────────────────────────
    ctx.font = `600 11px ${FONT.family}`;
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.textAlign = 'center';
    ctx.fillText('VENSTRE HEMISFÆR',  cx - w * 0.23, cy - rY - 12);
    ctx.fillText('ANALYTISK · JURIDISK', cx - w * 0.23, cy - rY - 0);
    ctx.fillText('HØJRE HEMISFÆR', cx + w * 0.23, cy - rY - 12);
    ctx.fillText('RELATION · KOMMUNIKATION', cx + w * 0.23, cy - rY - 0);
    ctx.textAlign = 'left';

    ctx.restore();
  }, [dims, soegTekst]);

  // ── Tegn ved dims-ændring ────────────────────────────────────────────────
  useEffect(() => {
    tegn();
  }, [tegn, dims]);

  // ── Zoom og pan via D3 ───────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const zoom = d3.zoom()
      .scaleExtent([0.15, 8])
      .on('zoom', (event) => {
        transformRef.current = event.transform;
        tegn();
      });

    d3.select(canvas).call(zoom);
    zoomRef.current = zoom;
    return () => { d3.select(canvas).on('.zoom', null); };
  }, [tegn]);

  // ── Mouse-hover ──────────────────────────────────────────────────────────
  const findNaerNode = useCallback((mx, my) => {
    const t = transformRef.current;
    const wx = (mx - t.x) / t.k;
    const wy = (my - t.y) / t.k;

    let narmest = null;
    let minDist  = 16; // pixel-threshold

    for (const n of nodeRef.current) {
      if (!n.x || !n.y) continue;
      const dx = n.x - wx;
      const dy = n.y - wy;
      const d  = Math.sqrt(dx * dx + dy * dy);
      if (d < minDist) { minDist = d; narmest = n; }
    }
    return narmest;
  }, []);

  const handleMouseMove = useCallback((e) => {
    const canvas  = canvasRef.current;
    const rect    = canvas?.getBoundingClientRect();
    if (!rect) return;
    const mx  = e.clientX - rect.left;
    const my  = e.clientY - rect.top;
    const nod = findNaerNode(mx, my);
    hovNodRef.current = nod || null;
    canvas.style.cursor = nod ? 'pointer' : 'default';
    tegn();
  }, [findNaerNode, tegn]);

  const handleClick = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect   = canvas?.getBoundingClientRect();
    if (!rect) return;
    const nod = findNaerNode(e.clientX - rect.left, e.clientY - rect.top);
    if (nod) setValgtNode(nod);
  }, [findNaerNode]);

  // ── Søg: opdater matchAntal ───────────────────────────────────────────────
  useEffect(() => {
    if (soegTekst.trim().length < 2) {
      setMatchAntal(null);
      tegn();
      return;
    }
    const q = soegTekst.toLowerCase().trim();
    const antal = nodeRef.current.filter(n => n.titel?.toLowerCase().includes(q)).length;
    setMatchAntal(antal);
    tegn();
  }, [soegTekst, tegn]);

  // ── Beriger noder med beskrivelse ved klik ────────────────────────────────
  const [detaljeNode, setDetaljeNode] = useState(null);

  useEffect(() => {
    if (!valgtNode) { setDetaljeNode(null); return; }
    // Hent beskrivelse fra alle-noder endpoint
    fetch(
      `/api/os/hjerne/alle-noder?q=${encodeURIComponent(valgtNode.titel?.slice(0,30) || '')}&limit=5&type=${valgtNode.type !== 'hjerne_fil' ? valgtNode.type : 'hjerne_fil'}`,
      { credentials: 'include' }
    )
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const match = (data?.noder || []).find(n => n.id === valgtNode.id);
        setDetaljeNode(match
          ? { ...valgtNode, beskrivelse: match.beskrivelse }
          : valgtNode
        );
      })
      .catch(() => setDetaljeNode(valgtNode));
  }, [valgtNode]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display:       'flex',
      flexDirection: 'column',
      gap:           12,
      fontFamily:    FONT.family,
    }}>
      {/* KPI-stribe */}
      <KpiStribe
        grafData={{ noder }}
        rawAntalNoder={vaekst?.firmaviden ?? null}
        rawAntalKanter={kanter.length > 0 ? kanter.length : (vaekst ? 306 : null)}
      />

      {/* Graf-container */}
      <div
        ref={containerRef}
        style={{
          position:     'relative',
          width:        '100%',
          height:       580,
          borderRadius: RADIUS.kort,
          overflow:     'hidden',
          background:   BG.app,
          border:       `1px solid ${KANT.standard}`,
        }}
      >
        {/* Canvas */}
        <canvas
          ref={canvasRef}
          width={dims.w}
          height={dims.h}
          onMouseMove={handleMouseMove}
          onClick={handleClick}
          style={{ display: 'block', width: '100%', height: '100%' }}
        />

        {/* Loading-overlay */}
        {loading && (
          <div style={{
            position:       'absolute',
            inset:          0,
            display:        'flex',
            flexDirection:  'column',
            alignItems:     'center',
            justifyContent: 'center',
            background:     'rgba(6,9,23,0.85)',
            zIndex:         20,
          }}>
            <div style={{
              width:         40,
              height:        40,
              borderRadius:  '50%',
              border:        `3px solid ${KANT.standard}`,
              borderTop:     `3px solid ${STATUS.ai}`,
              animation:     'spin 0.9s linear infinite',
              marginBottom:  14,
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ color: TEKST.sekundær, fontSize: 13 }}>
              Indlæser vidensgraf...
            </div>
          </div>
        )}

        {/* Fejl-overlay */}
        {fejl && (
          <div style={{
            position:   'absolute',
            inset:      0,
            display:    'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(6,9,23,0.80)',
            zIndex:     20,
          }}>
            <div style={{ color: STATUS.fejl, fontSize: 13, textAlign: 'center', padding: 24 }}>
              {fejl}
            </div>
          </div>
        )}

        {/* Søgefelt */}
        {!loading && (
          <Søgefelt
            soegTekst={soegTekst}
            onChange={setSoegTekst}
            matchAntal={matchAntal}
          />
        )}

        {/* Legend */}
        {!loading && Object.keys(typeAntal).length > 0 && (
          <Legend typeAntal={typeAntal} />
        )}

        {/* Detalje-panel */}
        {detaljeNode && (
          <DetaljePanel
            node={detaljeNode}
            kanter={kanter}
            alleNoder={noder}
            onLuk={() => { setValgtNode(null); setDetaljeNode(null); }}
          />
        )}

        {/* Hjælpe-tekst */}
        {!loading && !valgtNode && (
          <div style={{
            position:   'absolute',
            bottom:     16,
            right:      16,
            fontSize:   10,
            color:      TEKST.dæmpet,
            textAlign:  'right',
            lineHeight: 1.6,
            fontFamily: FONT.family,
          }}>
            Scroll: zoom · Træk: pan · Klik: detaljer
          </div>
        )}
      </div>

      {/* Forklaring */}
      <div style={{
        fontSize:   11,
        color:      TEKST.dæmpet,
        lineHeight: 1.5,
      }}>
        Viser op til {MAX_NODER.toLocaleString('da-DK')} udvalgte noder (top-relevante per type) ud af{' '}
        {vaekst?.firmaviden
          ? vaekst.firmaviden.toLocaleString('da-DK')
          : '34.097+'
        }{' '}
        totale viden-noder. Kanter fra os.vidensgraf ({kanter.length > 0
          ? kanter.length.toLocaleString('da-DK') + ' synlige'
          : '306 tilgængelige — hentes ved kant-endpoint'
        }).
      </div>
    </div>
  );
}

// ─── Default export: WebGL er STANDARD ───────────────────────────────────────
// WebGL-renderer vises altid når ny_os_dashboard_enabled er ON.
// D3-versionen (HjerneGrafPanelD3) er bevaret som fallback men bruges ikke.

export default function HjerneGrafPanel() {
  return (
    <React.Suspense fallback={
      <div style={{
        padding: 60, textAlign: 'center',
        color: '#7a8fa6',
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSize: 13,
      }}>
        Indlæser vidensgraf...
      </div>
    }>
      <HjerneGrafWebGLLazy />
    </React.Suspense>
  );
}
